// ─── SCORING ENGINE ───────────────────────────────────────────────────────────
// Shared by the 2D case runner (App.js) and the 3D/AR playground
// (CRRTPlayground.js).
//
// This previously lived only in App.js, so the playground had no way to score
// anything and Room 9 resolved through a detector written by hand for that one
// case. Extracting it is what lets every case render in AR without the logic
// being written twice and drifting apart.
//
//   evaluateRoomSuccess(caseData, ctx) -> { success, feedback, errorFlags, ... }
//     caseData  a case definition, as in BASE_CASES
//     ctx       { rx, actions, clBsa, clKg, netUfKgHr, efflKg, efflBsa, ffPct }
//
// No React and nothing browser-specific: the playground imports this inside a
// lazy chunk, and it should stay testable on its own.

const filterDefs = {
  HF20:   { ecv:85,  weightHint:"<15 kg",    brandLabel:"HF20",    genericLabel:"PES Filter Small" },
  ST60:   { ecv:118, weightHint:"15–30 kg",  brandLabel:"ST60",    genericLabel:"AN69 Filter Medium" },
  ST150:  { ecv:190, weightHint:"≥30 kg",    brandLabel:"ST150",   genericLabel:"AN69 Filter Large" },
  OXiris: { ecv:189, weightHint:"≥30 kg",    brandLabel:"OXiris",  genericLabel:"AN69 Cytokine Adsorptive" },
};

const gaveK   = (acts) => acts.includes("add_k_bag")   || acts.includes("supp_k");

const gavePO4 = (acts) => acts.includes("add_po4_bag") || acts.includes("supp_po4");

const calcClearanceBSA = (pbp, dial, rep, bsa) =>
  bsa>0?(((pbp+dial+rep)/1000)/bsa*1.73).toFixed(2):"—";

function resolveCriteria(caseData) {
  if (caseData.successType) return caseData; // already flat — base cases & overrides
  const criteria = Array.isArray(caseData.successCriteria) ? caseData.successCriteria : [];
  if (!criteria.length) return caseData;
  // First criterion wins as primary; multi-criteria evaluated via AND/OR in evaluator
  const first = criteria[0];
  const typeMap = {
    clearance_gte:         { successType:"clearance_gte",   successParam: first.params?.threshold ?? 1.9 },
    effluent_kg_gte:       { successType:"effluent_kg_gte", successParam: first.params?.threshold ?? 25 },
    effluent_min_of:       { successType:"effluent_min_of", successParam: {
                               mlKgHr: first.params?.mlKgHr ?? 90,
                               lBsaHr: first.params?.lBsaHr ?? 6 } },
    ff_lte:                { successType:"ff_lte",         successParam: {
                               maxFf:        first.params?.maxFf ?? 20,
                               minNetUfKgHr: first.params?.minNetUfKgHr ?? 0 } },
    clearance_lte:         { successType:"clearance_lte",   successParam: first.params?.threshold ?? 3.0 },
    bfr_lte:               { successType:"bfr_lte",         successParam: first.params?.threshold ?? 150 },
    netuf_kghr_lte:        { successType:"netuf_kghr_lte",  successParam: first.params?.threshold ?? 2.0 },
    intervention:          { successType:"intervention",    successParam: first.params?.id ?? "" },
    anticoag_not:          { successType:"anticoag_not",    successParam: first.params?.value ?? "" },
    access_not:            { successType:"access_not",      successParam: first.params?.value ?? "" },
    ecv:                   { successType:"ecv",             successParam: "" },
    electrolytes:          { successType:"electrolytes",    successParam: { k: first.params?.k ?? 3, po4: first.params?.po4 ?? 1 } },
    citrate_accum:         { successType:"citrate_accum",   successParam: "" },
    citrate_alkalosis:     { successType:"citrate_alkalosis",successParam:"" },
    epoprostenol:          { successType:"epoprostenol",    successParam: "" },
    na_add:                { successType:"na_add",          successParam: { target: first.params?.target ?? 174 } },
    free_water:            { successType:"free_water",      successParam: { target: first.params?.target ?? 126 } },
    clearance_lte_and_mannitol: { successType:"clearance_lte_and_mannitol", successParam: first.params?.threshold ?? 2.6 },
    room1:                 { successType:"room1",           successParam: "" },
  };
  const mapped = typeMap[first.type];
  if (!mapped) return caseData;

  // For multi-criteria with AND logic, we evaluate each and require all to pass
  if (criteria.length > 1) {
    return { ...caseData, _multiCriteria: criteria, criteriaLogic: caseData.criteriaLogic || "AND", ...mapped };
  }
  return { ...caseData, ...mapped };
}

function evaluateRoomSuccess(caseData, ctx) {
  const res  = _evaluateRoomSuccess(caseData, ctx);
  const acts = Array.isArray(ctx?.actions) ? ctx.actions : [];
  if (!res) return res;

  const naWrong = acts.includes("add_sodium")     && caseData?.successType !== "na_add";
  const fwWrong = acts.includes("add_free_water") && caseData?.successType !== "free_water";

  if (res.errorFlags) {
    if (naWrong) res.errorFlags.error_na_add     = "unnecessary";
    if (fwWrong) res.errorFlags.error_free_water = "unnecessary";
  }
  if (naWrong || fwWrong) {
    res.success = false;
    if (!res.feedback) {
      res.feedback = naWrong && fwWrong
        ? "Sodium and free water were both added to the solution. Neither is indicated here, and both change the tonicity of every bag the patient receives."
        : naWrong
          ? "Sodium was added to the solution. That is not indicated here, and it changes the tonicity of every bag the patient receives."
          : "Free water was added to the solution. That is not indicated here, and it changes the tonicity of every bag the patient receives.";
    }
  }
  return res;
}

function _evaluateRoomSuccess(caseData, ctx) {
  caseData = resolveCriteria(caseData);

  const { rx, actions, clBsa, clKg, netUfKgHr, efflKg, efflBsa, ffPct } = ctx || {};

  // Sodium and free water are available on every case now, so choosing one
  // where it is not indicated is a real decision and worth recording. Flagged,
  // not failed: the case's own criterion decides pass or fail.
  const _allActs = Array.isArray(actions) ? actions : [];
  const _naUnneeded = _allActs.includes("add_sodium")     && caseData?.successType !== "na_add";
  const _fwUnneeded = _allActs.includes("add_free_water") && caseData?.successType !== "free_water";
  const has     = (id) => Array.isArray(actions) && actions.includes(id);
  const bfr     = parseFloat(rx?.bfr || 0) || 0;
  const fallback = caseData.learningPoint || "Review the case details and try again.";

  // Null errorFlags template — all parameters out of scope by default
  const nullFlags = () => ({
    error_clearance:    null,
    error_netuf:        null,
    error_bfr:          null,
    error_anticoag:     null,
    error_solution:     null,
    error_access:       null,
    error_filter:       null,
    error_prime:        null,
    error_modality:     null,
    error_k_add:        null,
    error_po4_add:      null,
    error_na_add:       null,
    error_free_water:   null,
    error_intervention: null,
  });

  const st = (caseData.successType || "").trim();
  const sp = caseData.successParam;

  if (!st) return { success: false, feedback: fallback, errorFlags: nullFlags(), criteriaLogic: "single", chosenSolutionPath: null };

  // ── Multi-criteria (AND / OR) ─────────────────────────────────────────────
  if (caseData._multiCriteria && Array.isArray(caseData._multiCriteria)) {
    const logic = caseData.criteriaLogic || "AND";
    const CTYPE_MAP = (c) => ({
      clearance_gte:  { successType:"clearance_gte",  successParam: c.params?.threshold ?? 1.9 },
      effluent_kg_gte:{ successType:"effluent_kg_gte",successParam: c.params?.threshold ?? 25 },
      effluent_min_of:{ successType:"effluent_min_of",successParam: {
                          mlKgHr: c.params?.mlKgHr ?? 90,
                          lBsaHr: c.params?.lBsaHr ?? 6 } },
      ff_lte:         { successType:"ff_lte",         successParam: {
                          maxFf:        c.params?.maxFf ?? 20,
                          minNetUfKgHr: c.params?.minNetUfKgHr ?? 0 } },
      clearance_lte:  { successType:"clearance_lte",  successParam: c.params?.threshold ?? 3.0 },
      bfr_lte:        { successType:"bfr_lte",        successParam: c.params?.threshold ?? 150 },
      netuf_kghr_lte: { successType:"netuf_kghr_lte", successParam: c.params?.threshold ?? 2.0 },
      intervention:   { successType:"intervention",   successParam: c.params?.id ?? "" },
      anticoag_not:   { successType:"anticoag_not",   successParam: c.params?.value ?? "" },
      access_not:     { successType:"access_not",     successParam: c.params?.value ?? "" },
      electrolytes:   { successType:"electrolytes",   successParam: { k: c.params?.k ?? 3, po4: c.params?.po4 ?? 1 } },
    }[c.type]);

    const results = caseData._multiCriteria.map(c => {
      const m = CTYPE_MAP(c);
      if (!m) return { success: false, errorFlags: nullFlags() };
      return evaluateRoomSuccess({ ...caseData, _multiCriteria: null, ...m }, ctx);
    });

    if (logic === "OR") {
      // OR: pass if any branch satisfied. Do NOT assign error flags — use chosenSolutionPath.
      const passIdx = results.findIndex(r => r.success);
      const ok      = passIdx >= 0;
      const chosenSolutionPath = ok
        ? (caseData._multiCriteria[passIdx]?.type || null)
        : null;
      return {
        success: ok,
        feedback: ok ? "" : fallback,
        errorFlags: nullFlags(),      // all null for OR-logic — see schema note
        criteriaLogic: "OR",
        chosenSolutionPath,
      };
    }

    // AND: all must pass. Merge error flags — take worst per parameter.
    const ok     = results.every(r => r.success);
    const merged = nullFlags();
    results.forEach(r => {
      if (!r.errorFlags) return;
      Object.keys(merged).forEach(k => {
        const existing = merged[k];
        const incoming = r.errorFlags[k];
        // Promote from null → false → string (error string wins)
        if (incoming === null) return;
        if (existing === null || (existing === false && incoming !== false)) merged[k] = incoming;
      });
    });
    return {
      success: ok,
      feedback: ok ? "" : fallback,
      errorFlags: merged,
      criteriaLogic: "AND",
      chosenSolutionPath: null,
    };
  }

  // ── Single-criterion evaluation ──────────────────────────────────────────────
  // Each branch populates exactly the error fields relevant to that success type.
  // All other fields remain null (not in scope for this case).

  const flags = nullFlags();

  if (st === "room1") {
    // Full blank prescription — all parameters in scope.
    // Dose target is weight-based effluent (PBP + dialysate + replacement + net
    // UF) at a minimum of 25 ml/kg/hr, rather than the BSA convention.
    const netUfOk = isFinite(netUfKgHr) && netUfKgHr >= 1.0 && netUfKgHr <= 2.0;
    const clOk    = isFinite(efflKg) && efflKg >= 25;
    const cathOk  = ["9F","12F","13F","14F"].includes(rx?.access || "");
    const filtOk  = ["ST60","ST150"].includes(rx?.filter || "");
    const modOk   = !!rx?.modality;
    const ok = modOk && netUfOk && clOk && cathOk && filtOk;
    flags.error_clearance = clOk    ? false : "too_low";
    flags.error_netuf     = netUfOk ? false : (isFinite(netUfKgHr) && netUfKgHr > 2.0 ? "too_high" : "too_low");
    flags.error_access    = cathOk  ? false : "wrong_choice";
    flags.error_filter    = filtOk  ? false : "wrong_choice";
    flags.error_modality  = modOk   ? false : "missing";
    return { success: ok, feedback: ok ? "" : fallback, errorFlags: flags, criteriaLogic: "single", chosenSolutionPath: null };
  }

  // Filtration fraction ceiling. successParam: { maxFf: 20, minNetUfKgHr: 0.5 }
  // Several genuinely different prescriptions satisfy this — raise blood flow,
  // move replacement pre-filter, shift convective dose to dialysate, or reduce
  // post-filter volume — which is the point of the case. The net UF floor stops
  // the trivial "solution" of switching fluid removal off in a patient who is
  // fluid overloaded.
  if (st === "ff_lte") {
    const maxFf = parseFloat(sp?.maxFf ?? 20) || 20;
    const minUf = parseFloat(sp?.minNetUfKgHr ?? 0) || 0;
    const ffOk = isFinite(ffPct) && ffPct > 0 && ffPct <= maxFf;
    const ufOk = isFinite(netUfKgHr) && netUfKgHr >= minUf;
    const ok = ffOk && ufOk;
    flags.error_clearance = ffOk ? false : "too_high";
    flags.error_netuf     = ufOk ? false : "too_low";
    return { success: ok, feedback: ok ? "" : fallback, errorFlags: flags,
             criteriaLogic: "single", chosenSolutionPath: null };
  }

  // Weight-based effluent dose floor. Used where the teaching point is the
  // ml/kg/hr convention rather than the BSA one.
  if (st === "effluent_kg_gte") {
    const lim = parseFloat(sp || 0) || 0;
    const _acts = Array.isArray(actions) ? actions : [];
    const ok  = isFinite(efflKg) && efflKg >= lim && !gaveK(_acts);
    flags.error_clearance = (isFinite(efflKg) && efflKg >= lim) ? false : "too_low";
    if (gaveK(_acts)) flags.error_k_add = "unnecessary";
    return { success: ok, feedback: ok ? "" : fallback, errorFlags: flags, criteriaLogic: "single", chosenSolutionPath: null };
  }

  // Whichever of a weight-based and a BSA-based target is LOWER for this
  // patient. In small infants the two conventions diverge sharply, and taking
  // the lower avoids setting a target that is unreachable at that size.
  // successParam: { mlKgHr: 90, lBsaHr: 6 }
  if (st === "effluent_min_of") {
    const mlKg  = parseFloat(sp?.mlKgHr || 0) || 0;
    const lBsa  = parseFloat(sp?.lBsaHr || 0) || 0;
    const kgOk  = isFinite(efflKg)  && efflKg  >= mlKg;
    const bsaOk = isFinite(efflBsa) && efflBsa >= lBsa;
    const ok = kgOk || bsaOk;      // meeting the lower target satisfies the case
    flags.error_clearance = ok ? false : "too_low";
    return { success: ok, feedback: ok ? "" : fallback, errorFlags: flags, criteriaLogic: "single", chosenSolutionPath: null };
  }

  if (st === "clearance_gte") {
    const lim = parseFloat(sp || 0) || 0;
    const _acts = Array.isArray(actions) ? actions : [];
    const ok  = isFinite(clBsa) && clBsa >= lim && !gaveK(_acts);
    flags.error_clearance = ok ? false : (isFinite(clBsa) && clBsa >= lim ? false : "too_low");
    // Also flag K_add error if they added potassium when clearance case context says not to
    if (gaveK(_acts)) flags.error_k_add = "unnecessary";
    return { success: ok, feedback: ok ? "" : fallback, errorFlags: flags, criteriaLogic: "single", chosenSolutionPath: null };
  }

  if (st === "clearance_lte_and_mannitol") {
    const lim    = parseFloat(sp || 0) || 0;
    const clPass = isFinite(clBsa) && clBsa <= lim;
    const manPass = has("mannitol");
    const ok = clPass && manPass;
    flags.error_clearance    = clPass  ? false : (isFinite(clBsa) && clBsa > lim ? "too_high" : "too_low");
    flags.error_intervention = manPass ? false : "missing";
    return { success: ok, feedback: ok ? "" : fallback, errorFlags: flags, criteriaLogic: "single", chosenSolutionPath: null };
  }

  if (st === "bfr_lte") {
    const lim = parseFloat(sp || 0) || 0;
    const ok  = bfr > 0 && bfr <= lim;
    flags.error_bfr = ok ? false : (bfr > lim ? "too_high" : "too_low");
    return { success: ok, feedback: ok ? "" : fallback, errorFlags: flags, criteriaLogic: "single", chosenSolutionPath: null };
  }

  if (st === "netuf_kghr_lte") {
    const lim = parseFloat(sp || 0) || 0;
    const ok  = isFinite(netUfKgHr) && netUfKgHr > 0 && netUfKgHr <= lim;
    flags.error_netuf = ok ? false : (isFinite(netUfKgHr) && netUfKgHr > lim ? "too_high" : "too_low");
    return { success: ok, feedback: ok ? "" : fallback, errorFlags: flags, criteriaLogic: "single", chosenSolutionPath: null };
  }

  if (st === "intervention") {
    const ok = typeof sp === "string" && has(sp);
    flags.error_intervention = ok ? false : "missing";
    return { success: ok, feedback: ok ? "" : fallback, errorFlags: flags, criteriaLogic: "single", chosenSolutionPath: null };
  }

  if (st === "electrolytes") {
    // successParam: { k: true|false, po4: true|false }
    //   true  = this electrolyte MUST be replaced (bag or supplement)
    //   false = it must NOT be
    // Legacy numeric params (e.g. {k:3, po4:1}) are read as "required" when
    // greater than zero, so previously-authored cases keep working.
    const acts = Array.isArray(actions) ? actions : [];
    const req  = (v) => v === true || (typeof v === "number" && v > 0)
                      || (typeof v === "string" && parseFloat(v) > 0);
    const kReq = req(sp?.k);
    const pReq = req(sp?.po4);
    const kGiven = gaveK(acts);
    const pGiven = gavePO4(acts);

    const kOk = kReq ? kGiven : !kGiven;
    const pOk = pReq ? pGiven : !pGiven;
    const ok  = kOk && pOk;

    flags.error_k_add   = kOk ? false : (kReq ? "missing" : "unnecessary");
    flags.error_po4_add = pOk ? false : (pReq ? "missing" : "unnecessary");
    return { success: ok, feedback: ok ? "" : fallback, errorFlags: flags, criteriaLogic: "single", chosenSolutionPath: null };
  }

  if (st === "access_not") {
    const notVal = String(sp || "");
    const ok = !!(rx?.access) && rx.access !== notVal;
    flags.error_access = ok ? false : "wrong_choice";
    return { success: ok, feedback: ok ? "" : fallback, errorFlags: flags, criteriaLogic: "single", chosenSolutionPath: null };
  }

  if (st === "anticoag_not") {
    const notVal = String(sp || "");
    const ok = !!(rx?.anticoag) && rx.anticoag !== notVal;
    flags.error_anticoag = ok ? false : "wrong_choice";
    return { success: ok, feedback: ok ? "" : fallback, errorFlags: flags, criteriaLogic: "single", chosenSolutionPath: null };
  }

  if (st === "ecv") {
    // Filter+prime combination for small patient — both filter and prime in scope
    const fKey  = rx?.filter || "";
    const fDef  = filterDefs[fKey] || {};
    const ecv   = fDef.ecv || 0;
    const tbv   = (caseData.initialState?.weightKg || 1) * 80;
    const ratio = ecv / tbv;
    const prime = rx?.prime || "";
    let ok = false;
    if      (ratio < 0.10) ok = true;
    else if (ratio < 0.15) ok = prime === "albumin" || prime === "blood";
    else                   ok = prime === "blood";
    flags.error_filter = fKey ? false : "wrong_choice";  // filter is always in scope
    flags.error_prime  = ok   ? false : "wrong_choice";
    return { success: ok, feedback: ok ? "" : fallback, errorFlags: flags, criteriaLogic: "single", chosenSolutionPath: null };
  }

  if (st === "citrate_alkalosis") {
    // OR-equivalent single type: reduce BFR OR switch solution OR increase clearance OR pause
    // Treated as OR — use chosenSolutionPath, no error flags
    const initBfr    = parseFloat(caseData.initialPrescription?.bfr || 150);
    const reducedBfr = bfr > 0 && bfr <= initBfr * 0.70;
    const lowBicarb  = ["Prism0cal B22","Phoxilium B22K4"].includes(rx?.solution || "");
    const ip         = caseData.initialPrescription || {};
    const initCl     = parseFloat(calcClearanceBSA(
      parseFloat(ip.pbpRate||0)||0, parseFloat(ip.dialysateRate||0)||0,
      parseFloat(ip.repRate||0)||0, caseData.initialState?.bsa||1)) || 0;
    const biggerCl   = isFinite(clBsa) && clBsa >= initCl * 1.40;
    const paused     = has("pause_citrate") && has("pause_calcium");
    const ok         = reducedBfr || lowBicarb || biggerCl || paused;
    const chosenSolutionPath = !ok ? null
      : reducedBfr ? "bfr_reduction"
      : lowBicarb  ? "solution_switch"
      : biggerCl   ? "clearance_increase"
      : "pause_citrate_calcium";
    return { success: ok, feedback: ok ? "" : fallback, errorFlags: nullFlags(), criteriaLogic: "OR", chosenSolutionPath };
  }

  if (st === "citrate_accum") {
    // OR-equivalent single type
    const switched   = (rx?.anticoag || "") !== "citrate";
    const paused     = has("pause_citrate") && has("pause_calcium");
    const initBfr    = parseFloat(caseData.initialPrescription?.bfr || 120);
    const reducedBfr = bfr > 0 && bfr <= initBfr * 0.70;
    const ip         = caseData.initialPrescription || {};
    const initCl     = parseFloat(calcClearanceBSA(
      parseFloat(ip.pbpRate||0)||0, parseFloat(ip.dialysateRate||0)||0,
      parseFloat(ip.repRate||0)||0, caseData.initialState?.bsa||1)) || 0;
    const biggerCl   = isFinite(clBsa) && clBsa >= initCl * 1.40;
    const ok         = switched || paused || reducedBfr || biggerCl;
    const chosenSolutionPath = !ok ? null
      : switched   ? "anticoag_switch"
      : paused     ? "pause_citrate_calcium"
      : reducedBfr ? "bfr_reduction"
      : "clearance_increase";
    return { success: ok, feedback: ok ? "" : fallback, errorFlags: nullFlags(), criteriaLogic: "OR", chosenSolutionPath };
  }

  if (st === "epoprostenol") {
    // OR: switch anticoag OR lower dose
    const switched = (rx?.anticoag || "") !== "epoprostenol";
    const lowered  = has("lower_epoprostenol");
    const ok = switched || lowered;
    const chosenSolutionPath = !ok ? null : switched ? "anticoag_switch" : "dose_reduction";
    return { success: ok, feedback: ok ? "" : fallback, errorFlags: nullFlags(), criteriaLogic: "OR", chosenSolutionPath };
  }

  // successParam: { target: <desired solution Na>, bagL: <bag volume, default 5> }
  // The required additive is (target - solution Na) x bag volume in litres.
  // Previously this accepted any non-zero amount, so a learner adding 1 mmol
  // passed a case about controlled correction. Now checked to within 10%.
  if (st === "na_add") {
    const _acts = Array.isArray(actions) ? actions : [];
    // The action must be selected AND dosed. Typing a number without choosing
    // to add sodium is not a prescription. Free water is handled by the wrapper
    // above, which fails it as inappropriate on this case.
    if (!_acts.includes("add_sodium")) {
      flags.error_na_add = "missing";
      return { success: false, feedback: fallback, errorFlags: flags, criteriaLogic: "single", chosenSolutionPath: null };
    }
    const naAdd  = parseFloat(rx?.Na_add || 0) || 0;
    const target = parseFloat(sp?.target || 0) || 0;
    const bagL   = parseFloat(sp?.bagL ?? 5) || 5;
    const solNa  = parseFloat(sp?.solutionNa ?? 140) || 140;
    const needed = (target - solNa) * bagL;
    const ok = needed > 0 && naAdd >= needed * 0.9 && naAdd <= needed * 1.1;
    flags.error_na_add = ok ? false
      : naAdd <= 0 ? "missing"
      : naAdd < needed * 0.9 ? "too_low" : "too_high";
    return { success: ok, feedback: ok ? "" : fallback, errorFlags: flags, criteriaLogic: "single", chosenSolutionPath: null };
  }

  // successParam: { target: <desired solution Na>, bagL: <bag volume, default 5> }
  // Free water required = bag volume x (solution Na - target) / target.
  // Checked to within 10%, for the same reason as na_add above.
  if (st === "free_water") {
    const _acts = Array.isArray(actions) ? actions : [];
    if (!_acts.includes("add_free_water")) {
      flags.error_free_water = "missing";
      return { success: false, feedback: fallback, errorFlags: flags, criteriaLogic: "single", chosenSolutionPath: null };
    }
    const fwAdd  = parseFloat(rx?.freeWater_add || rx?.freeWater || 0) || 0;
    const target = parseFloat(sp?.target || 0) || 0;
    const bagML  = (parseFloat(sp?.bagL ?? 5) || 5) * 1000;
    const solNa  = parseFloat(sp?.solutionNa ?? 140) || 140;
    const needed = target > 0 ? bagML * (solNa - target) / target : 0;
    const ok = needed > 0 && fwAdd >= needed * 0.9 && fwAdd <= needed * 1.1;
    flags.error_free_water = ok ? false
      : fwAdd <= 0 ? "missing"
      : fwAdd < needed * 0.9 ? "too_low" : "too_high";
    return { success: ok, feedback: ok ? "" : fallback, errorFlags: flags, criteriaLogic: "single", chosenSolutionPath: null };
  }

  // Unknown type — fail safely, no flags
  return { success: false, feedback: fallback, errorFlags: nullFlags(), criteriaLogic: "single", chosenSolutionPath: null };
}

export {
  evaluateRoomSuccess,
  resolveCriteria,
  filterDefs,
  calcClearanceBSA,
  gaveK,
  gavePO4,
};
